const en = {
  header: {
    home: 'Onnix Dev — home', openMenu: 'Open menu', closeMenu: 'Close menu',
    navigation: 'Main navigation', language: 'Language',
    solutions: 'Solutions', projects: 'Work', about: 'About Onnix', contact: "Let's talk",
  },
  hero: {
    eyebrow: 'Onnix Dev · Independent software studio',
    titleStart: 'Websites, software, and automation', titleBridge: 'that move', titleAccent: 'your business forward.',
    description: 'We build digital solutions for small businesses, clinics, and service providers looking to attract customers, streamline operations, and reduce manual work.',
    primaryCta: "Let's talk about your project", secondaryCta: 'Explore our work',
    capabilitiesLabel: 'What we build',
    capabilities: [
      { label: '01 / Digital presence', title: 'Websites and landing pages', description: 'Present your business clearly and make it easier for new customers to reach you.' },
      { label: '02 / Your operations', title: 'Software and scheduling', description: 'Organize your workflows around your business, rather than a generic tool.' },
      { label: '03 / Connected workflows', title: 'Automation and integrations', description: 'Reduce manual tasks by connecting WhatsApp, calendars, and software.' },
    ],
  },
  value: {
    eyebrow: 'From customers to operations', title: 'A clearer path for customers. Less manual work for your team.',
    description: 'When your digital tools do not support the way you work, customers face friction and your team has to fill the gaps manually.',
    link: 'See how we can help', listLabel: 'Where technology can help',
    outcomes: [
      { title: 'Make the first step easier', problem: 'When services and contact options are hard to find, customers may not know where to start.', result: 'Keep information and contact options in one accessible place.' },
      { title: 'Organize day-to-day service', problem: 'Schedules, requests, and customer details in separate records create repeated checks and tasks.', result: 'A more organized way to manage daily work.' },
      { title: 'Keep work moving', problem: 'Passing information between tools and individual records can create rework.', result: 'Connected processes with fewer manual handoffs.' },
    ],
  },
  services: {
    eyebrow: 'What we build', title: 'Solutions for your digital presence and operations.',
    intro: 'From a professional website to a more organized workflow, every project starts with what your business actually needs.',
    solutionLabel: 'How we can help', contactTitle: 'Not sure where to start?',
    contactDescription: 'Tell us how your business works. We will understand the need before defining the scope.',
    contactCta: 'Talk through a solution',
    items: [
      {
        id: 'sites', title: 'Websites & Landing Pages',
        problem: 'Your business needs to build trust and make it easy for new customers to get in touch.',
        solution: 'We build responsive company websites, sales pages, and landing pages around your business needs.',
        listLabel: 'Designed to support',
        highlights: ['A professional online presence', 'Clear presentation of your services', 'Easy ways to get in touch', 'A foundation for new enquiries'],
        scope: 'Forms, traffic tracking, and connections to lead tools can be considered when defining the scope.',
        contactLabel: 'Discuss a website',
        message: 'Hi Onnix Dev! I would like to discuss a website or landing page for my business.',
      },
      {
        id: 'sistemas', title: 'Custom Software',
        problem: 'Spreadsheets and disconnected tools can make your operations harder to manage.',
        solution: 'We build web software to organize the processes, information, and routines specific to your business.',
        listLabel: 'Designed to support',
        highlights: ['More organized processes', 'Information in one place', 'Less manual work', 'A solution shaped around your business'],
        scope: 'Management dashboards, inventory controls, and private portals are examples of features to define together.',
        contactLabel: 'Discuss custom software',
        message: 'Hi Onnix Dev! I would like to discuss custom software to organize my business operations.',
      },
      {
        id: 'agendamentos', title: 'Smart Scheduling',
        problem: 'Managing appointments and requests manually can take valuable time from your team.',
        solution: 'We build scheduling solutions with features defined around your workflow and service needs.',
        listLabel: 'Possible features',
        highlights: ['Availability management', 'Appointment requests', 'Confirmations', 'Service coordination'],
        scope: 'Features and any integrations depend on technical validation and the agreed project scope.',
        contactLabel: 'Discuss scheduling',
        message: 'Hi Onnix Dev! I would like to discuss a scheduling system for my business.',
      },
      {
        id: 'automacoes', title: 'Automation & Integrations',
        problem: 'Repetitive tasks and information spread across tools can lead to unnecessary rework.',
        solution: 'We design automation and integrations to connect processes and reduce manual work, subject to the capabilities of the tools involved.',
        listLabel: 'Possible features',
        highlights: ['Task automation', 'Connections between tools', 'Organized information', 'Custom workflows'],
        scope: 'Connections to WhatsApp, CRM, ERP, or spreadsheets are assessed case by case.',
        contactLabel: 'Discuss automation',
        message: 'Hi Onnix Dev! I would like to explore automation and integrations to reduce manual tasks in my business.',
      },
    ],
  },
  portfolio: {
    eyebrow: 'Selected work', title: 'Solutions that show how we work.',
    intro: 'From a well-presented online presence to tools for daily operations, each project starts with a specific need.',
    kinds: { commercial: 'Client project', own: 'In-house project', demo: 'Demonstration project' },
    imagePlaceholder: 'Project image in preparation', challenge: 'Challenge', importance: 'Why it matters',
    capabilitiesLabel: 'Project features', demoLink: 'View demo', demoAria: 'View demo for', repositoryLink: 'Explore repository', repositoryAria: 'Explore repository for', newTab: 'opens in a new tab',
    contactEyebrow: "Let's talk", contactTitle: 'Your business could be our next project.',
    contactDescription: 'From a professional website to custom software, let us understand what your operations need.',
    contactCta: "Let's talk about your project",
  },
  comparison: {
    eyebrow: 'Finding the right fit', title: 'Spreadsheets, off-the-shelf software, or Onnix Dev?',
    cards: [
      { title: 'Spreadsheets', points: ['Knowledge stays with specific people', 'No logs or alerts', 'Hard to integrate', 'Ongoing risk of manual errors'] },
      { title: 'Off-the-shelf software', points: ['Your workflow adapts to the tool', 'Limited consultative support', 'Slow and costly customization', 'Features your team may never use'] },
      { title: 'Onnix Dev', points: ['Architecture shaped around your needs', 'Automation across channels', 'Direct contact with the developer', 'Weekly delivery cycles'] },
    ],
    contactTitle: "Let's talk about ways to organize your operations.", contactCta: 'Explore the possibilities',
  },
  fit: {
    eyebrow: 'An honest fit', title: 'A good partnership starts with the right fit.',
    notFor: { title: 'Probably not the right fit if you', points: ['Need the lowest-cost ready-made option', 'Are not ready to define your processes', 'Prefer to keep everything manual'] },
    for: { title: 'A good fit if you', points: ['Want to free your team from repetitive work', 'Need reliable integrations', 'Value direct support and clear progress', 'Assess the return on technology investments', 'Need to make more room for higher-value work'] },
  },
  process: {
    eyebrow: 'Process', title: 'A clear, focused process built around useful progress.',
    steps: [
      { title: 'Focused discovery', description: 'We learn your current workflows, identify bottlenecks, and agree on what the project should improve.' },
      { title: 'Approved blueprint', description: 'You review a navigable prototype and the proposed architecture before development begins.' },
      { title: 'Iterative development', description: 'Weekly cycles, visible deliverables, and direct communication keep the work transparent.' },
      { title: 'Supported launch', description: 'We monitor the first days of use, make adjustments, and stay close as the solution goes live.' },
      { title: 'Ongoing improvement', description: 'We refine the solution as usage data and your operations evolve.' },
    ],
  },
  about: {
    eyebrow: 'The person behind the work', title: 'Onnix Dev is an independent software studio.',
    description: 'Led by Gabriel Fernandes, a developer focused on automation and web software. A lean studio means direct collaboration and solutions shaped around how each business actually works.',
    why: 'Why work with Onnix?',
    points: ['A focus on business value in each development cycle.', 'Clean, fast design informed by thoughtful software experiences.', 'Direct support from the person building your solution.'],
  },
  contact: {
    eyebrow: "Let's talk", title: 'Your next project starts with a conversation.',
    description: 'Tell us what your business needs. We will understand the challenge and explore a digital solution that makes sense for you.',
    primaryCta: "Let's talk about your project", emailCta: 'Send an email', nextStep: 'How we begin',
    steps: ['Tell us what you need. You do not need to define the technical solution first.', 'We discuss the possibilities and agree on the next steps.'],
  },
  footer: {
    description: 'Technology to simplify processes and help businesses move forward.',
    navigation: 'Navigation', navigationLabel: 'Footer navigation', home: 'Home', services: 'Services', projects: 'Work', contact: 'Contact',
    solutions: 'Solutions', explore: 'Explore solutions', contactTitle: 'Get in touch',
    serviceNames: ['Websites & Landing Pages', 'Custom Software', 'Smart Scheduling', 'Automation & Integrations'],
    independent: 'Independent software studio.',
  },
  contactMessages: {
    general: 'Hi! I found Onnix Dev through your website and would like to discuss a project.',
    services: 'Hi! I am interested in Onnix Dev solutions and would like to discuss what could help my business.',
    portfolio: 'Hi! I saw the Onnix Dev projects and would like to discuss a similar solution for my business.',
    consultation: 'Hi! I would like to explore ways to organize my business operations.',
  },
  projectText: {
    'louise-soares': {
      category: 'Website for in-person courses', stage: 'Local preview',
      description: 'A website that presents in-person aesthetics courses and makes the first contact easier.',
      problem: 'Bring courses, practical information, and contact options together in one clear experience.',
      solution: 'Responsive website with a course catalog, gallery, frequently asked questions, and location details.',
      value: 'Visitors can find courses, answers, and contact options in one place.',
      capabilities: ['Course catalog', 'Gallery and FAQs', 'Location and contact'],
      imageAlt: 'Louise Soares website home page with a callout for in-person courses and a portrait of the professional.',
      imageCaption: 'Home page · local capture',
    },
    agendaflow: {
      category: 'Scheduling software',
      description: 'A dashboard that brings appointments, customers, and services into one workflow.',
      problem: 'Keep track of availability and appointments when information is spread across records.',
      solution: 'Web software with a calendar, customer and service records, and an admin view.',
      value: 'The team can see appointments, customers, and services from one dashboard.',
      capabilities: ['Scheduling and availability', 'Customers and services', 'Admin dashboard'],
      imageAlt: 'AgendaFlow login screen with email and password fields.',
      imageCaption: 'Login screen · local preview',
    },
    autolanche: {
      category: 'Operations software',
      description: 'Ingredient, product, and sales management for a food service operation.',
      problem: 'Tracking inventory and sales in separate tools makes day-to-day work harder.',
      solution: 'Software to record ingredients, inventory movements, products, sales, and reports.',
      value: 'Inventory and sales can be followed in the same workflow.',
      capabilities: ['Ingredient inventory', 'Products and sales', 'Operational reports'],
      imageAlt: 'AutoLanche admin dashboard with zeroed indicators and shortcuts for daily operations.',
      imageCaption: 'Dashboard with no data · local preview',
    },
    'mais-rs': {
      category: 'Presentation page',
      description: 'A page that presents plans, helps visitors compare options, and directs enquiries to WhatsApp.',
      problem: 'Explain the available plans and make the next contact step simpler.',
      solution: 'Landing page with plan comparison, FAQs, and a form that prepares a WhatsApp conversation.',
      value: 'Visitors can compare options before sending an enquiry.',
      capabilities: ['Plan presentation', 'Option comparison', 'WhatsApp contact'],
      imageAlt: 'Mais RS home page introducing the commercial guide and inviting visitors to explore plans.',
      imageCaption: 'Home page · local capture',
    },
    'karise-estetica': {
      category: 'Appointment management',
      description: 'Software to organize aesthetics appointments, professionals, and services.',
      problem: 'Bring the calendar and records needed for daily service into one place.',
      solution: 'Dashboard for customers, professionals, services, appointments, and a calendar.',
      value: 'The schedule and records are available in the same daily workflow.',
      capabilities: ['Calendar and appointments', 'Customers and professionals', 'Service records'],
      imageAlt: 'Karise Estética dashboard with shortcuts to appointments, customers, services, and an empty calendar.',
      imageCaption: 'Dashboard with no data · local preview',
    },
  },
};

export default en;
